// @ts-nocheck

import {
    removePaymentProfile, selectPaymentProfile, setAsDefaultProfile, setBillingAddress,
    setBillingAddressSameAsContact, setLoading, setRequestError, setUseSaved,
    stripePaymentMethodRequiresReauthorization, paymentProfileIsDeprecated,
    formatStripeCardBrandLabel,
} from 'actions/paymentActions';
import _ from 'lodash';
import { reformatApiAddress } from 'modules/utils';
import React from 'react';
import { Button, ButtonGroup, Col, Form, Modal, Row, Dropdown, Badge } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { withRouter } from 'src/routes/WithRouter';
import Address from 'sharedComponents/Address';

import type { RootState } from 'src/store'
import type { AppRouteProps, InitialUrlParams } from 'src/types/app'

const getPaymentProfileId = (paymentProfile) => (
  paymentProfile?.id || paymentProfile?.customerPaymentProfileId || null
)

const findPaymentProfileById = (paymentProfiles, profileId) => (
  paymentProfiles.find(p => getPaymentProfileId(p) === profileId) || null
)

const findFirstSelectablePaymentProfile = (paymentProfiles) => (
  paymentProfiles.find(p => !stripePaymentMethodRequiresReauthorization(p)) || null
)

const resolveNextPaymentProfileId = (paymentProfiles, subscription) => {
  if (!paymentProfiles.length) return null

  const usedPaymentProfileId = subscription
    ? (
      subscription.paymentGateway === 'Authorize.NET'
        ? _.get(subscription, ['profile', 'paymentProfile', 'customerPaymentProfileId'])
        : (_.get(subscription, ['payment', 'id']) || subscription.stripe_payment_source_id__c)
    )
    : null

  if (usedPaymentProfileId) {
    const usedProfile = findPaymentProfileById(paymentProfiles, usedPaymentProfileId)
    if (usedProfile && !stripePaymentMethodRequiresReauthorization(usedProfile)) {
      return getPaymentProfileId(usedProfile)
    }
  }

  const firstSelectable = findFirstSelectablePaymentProfile(paymentProfiles)
  return firstSelectable ? getPaymentProfileId(firstSelectable) : null
}

const getPaymentProfileDescription = (paymentProfile, defaultSource) => {
  if (paymentProfile.gateway === 'Stripe') {
    const accountType = _.get(paymentProfile, ['metadata', 'account_type'], '')
    const isCard = paymentProfile.object === 'card' || paymentProfile.card
    const card = _.get(paymentProfile, 'card', paymentProfile)
    return (isCard ? (
      `${formatStripeCardBrandLabel(card.brand)} •••• ${card.last4}, Exp. ${card.exp_month}/${card.exp_year}`
    ) : (
      `Bank account${accountType ? (' (' + accountType + ')') : ''} •••• ${paymentProfile.last4}`
    )) + ((defaultSource && defaultSource === paymentProfile.id) ? ' (Default)' : '')
  }

  const isCard = !!paymentProfile.payment.creditCard
  const expiry = isCard ? `Exp. ${paymentProfile.payment.creditCard.expirationDate.slice(5, 7)}/${paymentProfile.payment.creditCard.expirationDate.slice(0, 4)}` : ''
  return (paymentProfile.payment.creditCard ? (
    `${paymentProfile.payment.creditCard.cardType} •••• ${paymentProfile.payment.creditCard.cardNumber}, ${expiry}`
  ) : (
    `Bank Account (${paymentProfile.payment.bankAccount.accountType}) •••• ${paymentProfile.payment.bankAccount.accountNumber}`
  )) + (paymentProfile.defaultPaymentProfile ? ' (Default)' : '')
}

const connector = connect((store: RootState, props: AppRouteProps) => ({
  customer: store.root.customer,
  recaptchaLoaded: store.root.recaptchaLoaded,
  showDetails: store.root.showDetails,
  contactAddress: store.account.contact, //store.contact.address,
  billingAddress: store.payment.billingAddress,
  paymentProfiles: store.payment.paymentProfiles,
  selectedPaymentProfileId: store.payment.selectedPaymentProfileId,
  useSaved: store.payment.useSaved,
  billingAddressSameAsContact: store.payment.billingAddressSameAsContact,
  asDefaultProfile: store.payment.asDefaultProfile,
  subscription: store.options.donationType === 'monthly' ? store.root.subscription : (
    store.options.donationType === 'yearly' ? store.root.subscriptionYearly : null
  ),
  defaultSource: (store.root.customer || {}).default_source,
  displayValidationError: store.payment.displayValidationError,
  paymentGateway: store.payment.gateway,
  paymentType: store.payment.type,
  donationType: store.options.donationType,
  services: props.services,
}), {
  selectPaymentProfile,
  setLoading,
  setRequestError,
  setUseSaved,
  setBillingAddress,
  setBillingAddressSameAsContact,
  setAsDefaultProfile,
  removePaymentProfile,
})

class PaymentProfiles extends React.Component {

  state = {
    reauthModalOpen: false,
    reauthProfileId: null,
  }

  setNextPaymentProfileIdBySubscription = () => {
    const { subscription, selectPaymentProfile, setUseSaved, paymentProfiles } = this.props
    const nextPaymentProfileId = resolveNextPaymentProfileId(paymentProfiles, subscription)
    if (nextPaymentProfileId) {
      selectPaymentProfile(nextPaymentProfileId)
      setUseSaved(true)
    } else {
      selectPaymentProfile(null)
      setUseSaved(false)
    }
  }

  clearLegacyPaymentProfileSelection = () => {
    const { paymentProfiles, selectedPaymentProfileId, selectPaymentProfile, setUseSaved } = this.props
    if (!selectedPaymentProfileId) return
    const selected = findPaymentProfileById(paymentProfiles, selectedPaymentProfileId)
    if (selected && stripePaymentMethodRequiresReauthorization(selected)) {
      selectPaymentProfile(null)
      setUseSaved(false)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      contactAddress, paymentProfiles, selectedPaymentProfileId, useSaved, billingAddressSameAsContact, subscription,
      selectPaymentProfile, setUseSaved, setBillingAddress, setBillingAddressSameAsContact, donationType, paymentGateway, paymentType,
    } = this.props
    const flattenedContactAddress = {
      ...contactAddress,
      ...contactAddress.address,
    }
    if (!prevProps.paymentProfiles.length && paymentProfiles.length) {
      this.setNextPaymentProfileIdBySubscription()
    }
    this.clearLegacyPaymentProfileSelection()
    if (!prevProps.useSaved && useSaved) {
      // Switch to use saved: if selected new payment method, need to reset
      // check pp id with subscription
      if (donationType === 'one-time') {
        if (!selectedPaymentProfileId) {
          const nextPaymentProfileId = getPaymentProfileId(findFirstSelectablePaymentProfile(paymentProfiles))
          if (nextPaymentProfileId) {
            selectPaymentProfile(nextPaymentProfileId)
          } else {
            selectPaymentProfile(null)
            setUseSaved(false)
          }
        }
      }
      else {
        this.setNextPaymentProfileIdBySubscription()
      }
    }
    if (prevProps.selectedPaymentProfileId !== selectedPaymentProfileId) {
      if (!selectedPaymentProfileId) {
        setBillingAddress(billingAddressSameAsContact ? contactAddress : null)
      }
      else {
        const selectedPaymentProfile = paymentProfiles.find(p => (p.customerPaymentProfileId || p.id) === selectedPaymentProfileId) || paymentProfiles[0]
        if (selectedPaymentProfile) {
          const nextBillingAddress = reformatApiAddress(selectedPaymentProfile.billTo || {
            name: (selectedPaymentProfile.name || '').trim(),
            line1: selectedPaymentProfile.address_line1 || '',
            city: selectedPaymentProfile.address_city || '',
            state: selectedPaymentProfile.address_state || '',
            postal_code: selectedPaymentProfile.address_zip || '',
            country: selectedPaymentProfile.address_country || '',
            ...(_.pick(selectedPaymentProfile.metadata, ['company', 'phone'])),
          }, selectedPaymentProfile.gateway)
          setBillingAddress(nextBillingAddress)
          const sameAsContact = Object.keys(nextBillingAddress).reduce((acc, cur) => (
            acc && (nextBillingAddress[cur] === flattenedContactAddress[cur])
          ), true)
          setBillingAddressSameAsContact(sameAsContact)
        }
      }
    }
    if (!prevProps.billingAddressSameAsContact && billingAddressSameAsContact) {
      setBillingAddress(flattenedContactAddress)
    }
    // if (!isNaN(selectedPaymentProfileId) && paymentGateway === 'Stripe' && (
    //   prevProps.donationType === 'one-time' && 
    //   donationType === 'monthly'
    // ) || (
    //   // Auto reset when a stripe card is selected for ACH
    //   prevProps.paymentType !== 'bank_ach' && paymentType === 'bank_ach'
    // )) {
    //   selectPaymentProfile(null)
    //   setUseSaved(false)
    // }
    if (prevProps.donationType === 'one-time' && donationType !== 'one-time' && subscription) {
      // one time switched to recurring
      const nextSelectedPaymentProfileId = subscription.paymentGateway === 'Authorize.NET' ? (
        subscription.authorize_net_payment_profile_id__c.split('-')[1]
      ) : subscription.stripe_payment_source_id__c
      const nextSelectedPaymentProfile = paymentProfiles.find(p => (p.customerPaymentProfileId || p.id) === nextSelectedPaymentProfileId) || (
        paymentProfiles.find(p => p.gateway === subscription.paymentGateway)
      )
      if (nextSelectedPaymentProfile && !stripePaymentMethodRequiresReauthorization(nextSelectedPaymentProfile)) {
        selectPaymentProfile(nextSelectedPaymentProfile.customerPaymentProfileId || nextSelectedPaymentProfile.id)
      }
      else {
        selectPaymentProfile(null)
        setUseSaved(false)
      }
    }
    if (
      prevProps.donationType !== 'one-time' &&
      this.props.donationType === 'one-time' &&
      this.props.paymentProfiles.length &&
      !this.props.selectedPaymentProfileId
    ) {
      const nextPaymentProfileId = getPaymentProfileId(findFirstSelectablePaymentProfile(this.props.paymentProfiles))
      if (nextPaymentProfileId) {
        this.props.selectPaymentProfile(nextPaymentProfileId)
      }
    }
    if (!this.props.useSaved && prevProps.useSaved) {
      this.props.selectPaymentProfile(null)
    }
  }

  renderPaymentMenu = paymentProfiles => {
    const { 
      selectedPaymentProfileId, 
      selectPaymentProfile, 
      defaultSource,
      setUseSaved,
      useSaved
    } = this.props
    if (!paymentProfiles.length) {
      return null
    }
    let selectedPaymentProfileDescription
    const menuItems = [...paymentProfiles, null].map(paymentProfile => {
      if (!paymentProfile) {
        return (
          <Dropdown.Item 
            key="add-new"
            active={!useSaved}
            onClick={() => {
              setUseSaved(false)
            }}
          >
            Add New Payment Method
          </Dropdown.Item>
        )
      }

      const paymentProfileId = getPaymentProfileId(paymentProfile)
      const requiresReauth = stripePaymentMethodRequiresReauthorization(paymentProfile)
      const paymentProfileDescription = getPaymentProfileDescription(paymentProfile, defaultSource)
      const deprecated = paymentProfileIsDeprecated(paymentProfile)
      const descStyle = deprecated ? { textDecoration: 'line-through', color: '#888' } : {}
      const selected = selectedPaymentProfileId === paymentProfileId

      if (selected && !requiresReauth) {
        selectedPaymentProfileDescription = paymentProfileDescription
      }

      if (requiresReauth) {
        return (
          <Dropdown.Item
            key={paymentProfileId}
            active={false}
            style={{ opacity: 0.75, cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              this.setState({ reauthModalOpen: true, reauthProfileId: paymentProfileId })
            }}
          >
            <span style={descStyle}>{paymentProfileDescription}</span>
            {' '}
            <Badge bg="warning" text="dark">Requires re-authorization</Badge>
          </Dropdown.Item>
        )
      }

      if (paymentProfile.gateway === 'Stripe') {
        return (
          <Dropdown.Item 
            key={paymentProfileId}
            active={selected}
            onClick={() => {
              selectPaymentProfile(paymentProfileId)
              setUseSaved(true)
            }}
          >
            <span style={descStyle}>{paymentProfileDescription}</span>
          </Dropdown.Item>
        )
      }

      return (
        <Dropdown.Item 
          key={paymentProfileId}
          active={selected}
          onClick={() => {
            selectPaymentProfile(paymentProfileId)
            setUseSaved(true)
          }}
        >
          <span style={descStyle}>{paymentProfileDescription}</span>
        </Dropdown.Item>
      )
    })
    return (
      <Dropdown>
        <Dropdown.Toggle>
          {selectedPaymentProfileDescription || 'Add New Payment Method'}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {menuItems}
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  componentDidMount() {
    const { paymentProfiles, subscription, selectPaymentProfile, setUseSaved } = this.props
    if (paymentProfiles.length) {
      const nextPaymentProfileId = resolveNextPaymentProfileId(paymentProfiles, subscription)
      if (nextPaymentProfileId) {
        selectPaymentProfile(nextPaymentProfileId)
        setUseSaved(true)
      } else {
        selectPaymentProfile(null)
        setUseSaved(false)
      }
    }
  }

  render() {
    const {
      showDetails,
      paymentProfiles,
      useSaved,
      selectPaymentProfile,
      setUseSaved,
      setRequestError,
      customer,
      paymentGateway,
      donationType,
      subscription,
      guestMode,
      layoutConfig,
      billingAddressSameAsContact,
      setBillingAddressSameAsContact,
      billingAddress,
      paymentErrors,
      invalidBillingAddressField,
      visibleInputs,
      displayValidationError,
      setBillingAddress,
      loggedInWithPhone,
    } = this.props
    // Sample of bank account data:
    // bankAccount: {accountType: "checking", routingNumber: "XXXX2610", accountNumber: "XXXX6789",…}
    // accountNumber: "XXXX6789"
    // accountType: "checking"
    // bankName: ""
    // echeckType: "WEB"
    // nameOnAccount: "Ken Xu"
    // routingNumber: "XXXX2610"

    if (!showDetails) {
      return null
    }
    const effectivePaymentProfiles = paymentProfiles.filter(p => 
      (p.payment || (p.gateway === 'Stripe')) &&
      (
        donationType === 'one-time' || !subscription || (
          subscription && (subscription.paymentGateway === p.gateway)
        )
      )
    )
    // Navigate to Payment Methods with ?reauth= so that page opens Add Payment Method (SetupIntent), per rebind UX
    const goReauthorize = () => {
      const reauthProfileId = this.state.reauthProfileId
      if (!reauthProfileId) return
      this.props.router.navigate(`/payment_methods?reauth=${reauthProfileId}`)
    }
    return (
      <div style={{ paddingBottom: 0 }}>
        <legend>Payment Information</legend>
        <Form.Group>
          {this.renderPaymentMenu(effectivePaymentProfiles)}
        </Form.Group>
        <Modal show={this.state.reauthModalOpen} onHide={() => this.setState({ reauthModalOpen: false, reauthProfileId: null })} centered>
          <Modal.Header closeButton>
            <Modal.Title>Payment method update needed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>This payment method needs to be re-authorized before it can be used again. Go to your payment methods to add a verified bank account or card.</p>
            <div className="d-flex justify-content-end">
              <Button type="button" className="btn-donate" onClick={goReauthorize}>
                Go to Payment Methods
              </Button>
            </div>
          </Modal.Body>
        </Modal>
        {
          (customer && !loggedInWithPhone) ? (
            <Form.Group>
              <LinkContainer to="/payment_methods"><a>{'Manage payment methods'}</a></LinkContainer>
            </Form.Group>
          ) : null
        }
        {
          (!guestMode && (layoutConfig?.contact?.billingAddressCheckbox?.visible !== false) && (!loggedInWithPhone || !customer?.accountId)) ? (
            <div className='mt1'>
              <Form.Check 
                id="billing-contact"
                className="pre-field flex-children"
                style={{marginTop: '0px'}}
              >
                <Form.Check.Input
                  type='checkbox'
                  checked={billingAddressSameAsContact} 
                  onChange={() => setBillingAddressSameAsContact(!billingAddressSameAsContact)}
                />
                <Form.Check.Label>
                  Billing address matches my Contact Address
                </Form.Check.Label>
              </Form.Check>
              {
                billingAddressSameAsContact ? null : (
                  <>
                  <h5>Billing Address</h5>
                  <Address
                    contactErrors={paymentErrors}
                    contact={{ ...billingAddress, address: billingAddress }}
                    invalidBillingAddressField={invalidBillingAddressField}
                    // address={billingAddress}
                    enterInfo={(field, value) => {
                      setBillingAddress({ [field]: value })
                    }}
                    id="billing-address-edit"
                    displayValidationError={displayValidationError}
                    layoutConfig={layoutConfig}
                    visibleInputs={visibleInputs}
                  />
                  </>
                )
              }
            </div>
          ) : null
        }
      </div>
    )
  }
}

export default withRouter(connector(PaymentProfiles))
